'use client'

import { useCallback } from 'react'
import { useWorkflow } from '@/context/workflow-context-simple'
import { useWorkflowApi } from '@/hooks/use-workflow-api'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'
import {
  buildVisibleViewIds,
  serializeStageFilters,
  serializeGlobalFilters,
} from '@/lib/view-save-helpers'

export interface SaveCurrentViewStateInput {
  /**
   * Stage configurations payload from the caller — only fieldConfigurationIds
   * needs to be supplied per stage. Pin / sort / show-completed are merged
   * in by the hook below from the current workflow state, so callers don't
   * have to remember to re-include them and risk clobbering the user's
   * choices server-side.
   */
  stageConfigurations: { stageConfigurationId: string; fieldConfigurationIds: string[] }[]
  /**
   * When provided, calls `updateVisibleColumns(...)` for an instant UI update
   * before the server round-trip lands. Should mirror the order of the field
   * ids the caller put into the current stage's stageConfigurations entry.
   */
  optimisticOrderedFieldIds?: string[]
}

/**
 * Persists the current user's personal view state (saveViewState endpoint) with
 * the supplied stageConfigurations, then refreshes the workflow configuration.
 * Single source of truth for the personal-view-state save flow that was
 * previously duplicated between components/edit-columns.tsx (`handleApply` for
 * isFromManageViews=false) and this hook's earlier siblings.
 *
 * Returns true on success. On failure the error is toasted and `false` is
 * returned so the caller can roll back any optimistic UI state.
 */
export function useSaveCurrentViewState() {
  const {
    configuration,
    currentView,
    stageFilters,
    globalFilters,
    state,
    setUnsavedChanges,
    updateVisibleColumns,
    refreshConfiguration,
    viewsResponse,
  } = useWorkflow()
  const { saveViewState } = useWorkflowApi()
  const { toast } = useToast()
  const { t } = useTranslation()
  // Hoist translations once per render — `t` itself is unstable across renders,
  // but the resolved strings are stable, so the callback can depend on those instead.
  const errorTitle = t('error')
  const failedToSaveViewMessage = t('failedToSaveView')

  return useCallback(
    async ({ stageConfigurations, optimisticOrderedFieldIds }: SaveCurrentViewStateInput): Promise<boolean> => {
      if (!configuration) return false

      // Merge in the per-stage display preferences from the current grid
      // state. Callers only need to supply fieldConfigurationIds; pin / sort
      // / show-completed are sticky and we don't want a column-list update
      // to reset them server-side.
      const pinnedByStage = state.pinnedColumnsByStage || {}
      const sortByStage = state.sortByStage || {}
      const showCompletedByStage = state.showCompletedByStage || {}
      const enrichedStageConfigurations = stageConfigurations.map(sc => ({
        ...sc,
        pinnedColumnId: pinnedByStage[sc.stageConfigurationId] || null,
        sortColumn: sortByStage[sc.stageConfigurationId]?.columnCode || null,
        sortDirection: sortByStage[sc.stageConfigurationId]?.direction || null,
        showCompleted: showCompletedByStage[sc.stageConfigurationId] ?? false,
      }))

      const request = {
        id: currentView?.viewSettingsId,
        title: currentView?.title,
        isPublic: currentView?.isPublic ?? false,
        isVisible: currentView?.isVisible ?? true,
        isDefault: currentView?.isDefault ?? false,
        stageConfigurations: enrichedStageConfigurations,
        stageFilters: serializeStageFilters(stageFilters),
        globalFilters: serializeGlobalFilters(globalFilters),
        visibleViewIds: buildVisibleViewIds(viewsResponse),
        columnWidths:
          state.columnWidths && Object.keys(state.columnWidths).length > 0
            ? JSON.stringify(state.columnWidths)
            : JSON.stringify({}),
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true,
      }

      try {
        await saveViewState(request)
        setUnsavedChanges(true)

        if (optimisticOrderedFieldIds) {
          const orderedFields = optimisticOrderedFieldIds
            .map(id => configuration.fieldsInUse.find(f => f.fieldConfigurationId === id))
            .filter((f): f is (typeof configuration.fieldsInUse)[0] => f !== undefined)
          updateVisibleColumns(orderedFields)
        }

        // skipFilterUpdate=true preserves current filters; useEffect handles data refresh.
        await refreshConfiguration(true, null, true)
        return true
      } catch (error) {
        toast({
          variant: 'destructive',
          title: errorTitle,
          description: error instanceof Error ? error.message : failedToSaveViewMessage,
        })
        return false
      }
    },
    [
      configuration,
      currentView,
      stageFilters,
      globalFilters,
      state.columnWidths,
      state.itemsPerPage,
      // Per-stage display preferences are merged into the payload above —
      // without them in the dep array the callback closes over stale maps
      // and persists yesterday's pin/sort/show-completed even though the
      // user has since changed all three. They're read on every call from
      // `state.pinnedColumnsByStage` etc., so the deps need to track them.
      state.pinnedColumnsByStage,
      state.sortByStage,
      state.showCompletedByStage,
      setUnsavedChanges,
      updateVisibleColumns,
      refreshConfiguration,
      viewsResponse,
      saveViewState,
      toast,
      errorTitle,
      failedToSaveViewMessage,
    ]
  )
}
